import { CampoMoeda } from '@/componentes/campo-moeda';
import { Input } from '@/componentes/ui/input';
import { Valor } from '@/componentes/valor';
import type { ChaveMes } from '@/dados';

import type { Cobertura } from './cobertura';
import { inteiroDe, NOMES_MESES, type LinhaMes } from './meses';

/* A lista de meses do histórico: uma linha por mês coberto. */

/* As quatro colunas da grade de meses. Cabeçalho e linhas usam a mesma régua.
   Esta é a EXCEÇÃO documentada à regra "o controle preenche a célula": numa
   lista de doze linhas iguais, campo esticado até a folga vira parede de
   caixas, então a TRILHA da grade carrega a medida do dado (contagem é campo
   curto, faturamento é campo médio) e o controle preenche essa trilha. A
   folga da linha sobra para a última coluna, que é leitura. */
const COLUNAS =
  'grid grid-cols-[4rem_var(--campo-curto)_var(--campo-medio)_minmax(6rem,1fr)] items-center gap-3';

export function Grade({
  chaves,
  cobertura,
  meses,
  aoMudarMes,
}: {
  chaves: readonly ChaveMes[];
  cobertura: Cobertura;
  meses: Record<ChaveMes, LinhaMes>;
  aoMudarMes: (chave: ChaveMes, linha: LinhaMes) => void;
}) {
  function mudarProjetos(chave: ChaveMes, texto: string) {
    const bruto = parseFloat(texto);
    const inteiro = inteiroDe(texto);
    // Fracionário é arredondado E refletido no campo: o número usado na conta
    // é sempre o número que está na tela.
    const ajustado = Number.isFinite(bruto) && bruto !== inteiro ? String(inteiro) : texto;
    aoMudarMes(chave, { ...meses[chave], projetos: ajustado });
  }

  /* UMA lista, um mês embaixo do outro. Já foi grade dupla, partida pelo
     breakpoint xl, que mede a JANELA: no casco de duas metades, janela 1280
     significa coluna de ~664px, e duas grades de 516px dentro de 664 era o
     scroll desta etapa. Doze linhas empilhadas rolam para BAIXO, que é a
     direção que rolagem pode ter.

     O overflow-x-auto fica como rede para tela estreita: se a linha não
     couber, quem rola é o bloco, nunca a página. */
  return (
    <div className="overflow-x-auto">
      <div className="grid">
        <Cabecalho />

        {chaves.map((chave, indice) => {
          // Com 12 meses os rótulos são de calendário. Com menos, histórico
          // parcial não tem âncora de calendário e chamar de "Jan" mentiria.
          const rotulo = cobertura === 12 ? NOMES_MESES[indice] : `Mês ${indice + 1}`;
          const projetos = inteiroDe(meses[chave].projetos);
          const valor = meses[chave].valor;
          const ticket = projetos > 0 && valor !== null && valor > 0 ? valor / projetos : null;

          // Mint é o realce, o mesmo do item apontado em menu e em select.
          // Areia diria "inerte", que é o contrário de "a linha em que você
          // está".
          return (
            <div
              key={chave}
              className={`${COLUNAS} border-fio focus-within:bg-mint border-t py-1.5`}
            >
              <span className="text-apoio font-medium">{rotulo}</span>
              <Input
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                placeholder="0"
                aria-label={`Projetos em ${rotulo}`}
                value={meses[chave].projetos}
                onChange={(evento) => mudarProjetos(chave, evento.currentTarget.value)}
                className="numero text-right"
              />
              <CampoMoeda
                valor={valor}
                aoMudar={(novo) => aoMudarMes(chave, { ...meses[chave], valor: novo })}
                aria-label={`Faturamento em ${rotulo}`}
              />
              <Valor
                valor={ticket}
                formato="moeda"
                tamanho="nota"
                tom={ticket === null ? 'apagado' : 'neutro'}
                className="justify-self-end"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Cabecalho() {
  return (
    <div className={`${COLUNAS} pb-1`}>
      <span className="rotulo">Mês</span>
      <span className="rotulo text-right">Projetos</span>
      <span className="rotulo text-right">Faturamento</span>
      <span className="rotulo text-right">Ticket médio</span>
    </div>
  );
}
